import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Hero } from "@/components/rentalmnp/landing/Hero";
import { ResumeBanner } from "@/components/rentalmnp/landing/ResumeBanner";
import { TrustBar } from "@/components/rentalmnp/landing/TrustBar";
import { Wizard } from "@/components/rentalmnp/landing/wizard/Wizard";
import { ScoreBanner } from "@/components/rentalmnp/results/ScoreBanner";
import { MetricsGrid } from "@/components/rentalmnp/results/MetricsGrid";
import { DiagnosticInsights } from "@/components/rentalmnp/results/DiagnosticInsights";
import { EmailPaywall } from "@/components/rentalmnp/results/EmailPaywall";
import { RegimeComparator } from "@/components/rentalmnp/unlocked/RegimeComparator";
import { TmiComparator } from "@/components/rentalmnp/unlocked/TmiComparator";
import { Optimizations } from "@/components/rentalmnp/unlocked/Optimizations";
import { FinalCTA } from "@/components/rentalmnp/unlocked/FinalCTA";
import { ActionButtons } from "@/components/rentalmnp/unlocked/ActionButtons";
import { AffiliatePartnersBlock } from "@/components/rentalmnp/unlocked/AffiliatePartnersBlock";
import { Footer } from "@/components/rentalmnp/shared/Footer";
import { useSimulation } from "@/components/rentalmnp/hooks/useSimulation";

// Lazy load heavy chart components — chargés seulement à l'écran B/C
const ChargesPieChart = lazy(() =>
  import("@/components/rentalmnp/results/ChargesPieChart").then((m) => ({
    default: m.ChargesPieChart,
  }))
);
const ProjectionChart = lazy(() =>
  import("@/components/rentalmnp/unlocked/ProjectionChart").then((m) => ({
    default: m.ProjectionChart,
  }))
);

const PAGE_TITLE = "Simulateur LMNP — Calcul rentabilité & régime fiscal";
const PAGE_DESC =
  "Calculez la vraie rentabilité de votre LMNP en 2 minutes. Cash-flow, rendement net, impôts, score d'investissement. Gratuit, sans inscription.";
const OG_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/723f6e41-7e9c-4b0e-b49b-612a28fb83d2/id-preview-f14ebb7c--29f067db-fe61-4add-9c48-2fa59addd215.lovable.app-1776593359439.png";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      { name: "keywords", content: "simulateur LMNP, calcul rentabilité LMNP, micro-BIC ou réel, comparateur LMNP, rendement locatif net, cash-flow LMNP" },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:url", content: "https://rentalmnp.com/" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://rentalmnp.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "RentaLMNP",
          alternateName: "Simulateur LMNP",
          url: "https://rentalmnp.com/",
          description: PAGE_DESC,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          inLanguage: "fr-FR",
          isAccessibleForFree: true,
          featureList: [
            "Calcul de la rentabilité brute, nette et nette-nette",
            "Comparateur micro-BIC vs régime réel",
            "Estimation du cash-flow mensuel",
            "Projection sur 10, 15 et 20 ans",
            "Simulation gratuite sans inscription",
          ],
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
          publisher: {
            "@type": "Organization",
            name: "RentaLMNP",
            url: "https://rentalmnp.com/",
          },
        }),
      },
    ],
  }),
  component: Index,
});

type Screen = "landing" | "results" | "unlocked";

function Index() {
  const sim = useSimulation();
  const [screen, setScreen] = useState<Screen>("landing");
  const [email, setEmail] = useState<string | null>(null);
  const [wizardStep, setWizardStep] = useState(1);
  const wizardRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll automatique vers le haut quand on change d'écran B/C
  useEffect(() => {
    if (screen === "results" || screen === "unlocked") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [screen]);

  // Charger une simulation depuis SavedSimulationsModal (via flag LocalStorage)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const pendingId = window.localStorage.getItem("rentalmnp:simulation:pending-load");
      if (!pendingId) return;
      const raw = window.localStorage.getItem("rentalmnp:simulations");
      if (!raw) return;
      const list = JSON.parse(raw) as Array<{ id: string; input: typeof sim.input }>;
      const found = list.find((s) => s.id === pendingId);
      window.localStorage.removeItem("rentalmnp:simulation:pending-load");
      if (found?.input) {
        sim.setInput(found.input);
        setScreen("landing");
        setTimeout(() => wizardRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      }
    } catch {
      /* no-op */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToWizard = () => {
    wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = () => setScreen("results");
  const handleUnlock = (email: string) => {
    setEmail(email);
    setScreen("unlocked");
  };
  const handleReset = () => {
    sim.reset();
    setEmail(null);
    setWizardStep(1);
    setScreen("landing");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const handleSwitchToDetailedCharges = () => {
    sim.update("chargesMode", "detaille");
    setWizardStep(3);
    setScreen("landing");
    setTimeout(() => wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  if (screen === "landing") {
    return (
      <div className="min-h-screen bg-background">
        <ResumeBanner onResume={scrollToWizard} />
        <Hero onCTA={scrollToWizard} />
        <TrustBar />
        <main ref={wizardRef}>
          <Wizard
            input={sim.input}
            update={sim.update}
            updateDetail={sim.updateDetail}
            results={sim.results}
            onSubmit={handleSubmit}
            initialStep={wizardStep}
          />
        </main>
        <Footer />
      </div>
    );
  }

  // Results & Unlocked partagent le bandeau + métriques + diagnostic + pie
  return (
    <div className="min-h-screen bg-background" ref={resultsRef}>
      <main>
        <ScoreBanner results={sim.results} />
        <MetricsGrid results={sim.results} hasCredit={sim.input.hasCredit} />
        <DiagnosticInsights insights={sim.insights} />
        <Suspense fallback={<div className="py-10 text-center text-sm text-muted-foreground">Chargement du graphique...</div>}>
          <ChargesPieChart
            results={sim.results}
            isSimpleMode={sim.input.chargesMode === "simple"}
            onSwitchToDetailed={handleSwitchToDetailedCharges}
          />
        </Suspense>

        {screen === "results" && (
          <EmailPaywall simulation={sim.input} onUnlock={handleUnlock} />
        )}

        {screen === "unlocked" && (
          <>
            <Suspense fallback={<div className="py-10 text-center text-sm text-muted-foreground">Chargement de la projection...</div>}>
              <ProjectionChart results={sim.results} input={sim.input} />
            </Suspense>
            <RegimeComparator results={sim.results} />
            <TmiComparator results={sim.results} input={sim.input} />
            <Optimizations optimizations={sim.optimizations} />
            <FinalCTA />
            <AffiliatePartnersBlock />
            <ActionButtons
              input={sim.input}
              results={sim.results}
              insights={sim.insights}
              optimizations={sim.optimizations}
              email={email}
              onReset={handleReset}
            />
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
